import { useEffect, useRef, useState } from 'react';
import TimedNotice from '../../timedNotice.js';
import { NATURE_LABELS } from '../../BioPanel.js';
import { displayLabel } from '../../displayLabel.js';
import type { CharacterAppearance, CharacterNature, WorldDraft } from '../../../domain/types.js';
import { WORLD_DOMAIN } from '../../../constants.js';
import { balancedLabel } from './aiSuggestions.js';
import { blankNature } from './defaults.js';
import { useAppearanceCatalog } from './useAppearanceCatalog.js';
import { validateAppearance, validateNature } from './validation.js';
import { OptionalContinueDialog, OptionalQuestionsToggle } from '../optionalQuestions.js';

export const SPECIES_OPTIONS = [
  'Human', 'Dog', 'Cat', 'Bird', 'Fish', 'Alien', 'Other',
] as const;

export default function CharacterDetailedScreen(props: {
  draft: WorldDraft;
  species?: string;
  background?: string;
  sex?: 'M' | 'F';
  draftCharacterId?: string;
  appearance: CharacterAppearance;
  nature: CharacterNature;
  error: string | null;
  onSpeciesChange?: (species: string) => void;
  onBackgroundChange?: (value: string) => void;
  onAppearanceChange: (appearance: CharacterAppearance) => void;
  onNatureChange: (nature: CharacterNature) => void;
  onAiSuggest: () => void;
  onBack: () => void;
  onSaveToast: () => void;
  onReset: () => void;
  onContinue: () => void;
  /** Saved character already has nature. Show it and do not replace it on Continue. */
  revealSavedNature?: boolean;
  onFillNature?: () => void;
  onValidationError?: (message: string) => void;
  /** Wizard footer owns the only Continue. The form must not render a second one. */
  shellOwnsContinue?: boolean;
  onRegisterContinue?: (run: (() => void) | null, disabled: boolean) => void;
}): JSX.Element {
  const species = props.species ?? 'Human';
  const draftCharacterId = props.draftCharacterId ?? 'draft-character';
  const { catalog, loading } = useAppearanceCatalog(props.draft, species, draftCharacterId, props.sex ?? 'M');
  const min = WORLD_DOMAIN.natureSliderMin;
  const max = WORLD_DOMAIN.natureSliderMax;
  const hairLabel = catalog.usesFurSlot ? 'Hair / fur' : 'Hair';
  const [optionalOpen, setOptionalOpen] = useState(props.revealSavedNature === true);
  const [optionalDialogOpen, setOptionalDialogOpen] = useState(false);
  const firstNatureRef = useRef<HTMLInputElement>(null);

  function handleContinue(): void {
    const appearanceErr = validateAppearance(props.appearance);
    if (appearanceErr) {
      props.onValidationError?.(appearanceErr);
      return;
    }
    const natureAlreadySaved = props.revealSavedNature === true && validateNature(props.nature) === null;
    if (!optionalOpen && !natureAlreadySaved) {
      setOptionalDialogOpen(true);
      return;
    }
    const natureErr = validateNature(props.nature);
    if (natureErr) {
      props.onValidationError?.(natureErr);
      return;
    }
    props.onContinue();
  }

  function handleOptionalYes(): void {
    props.onFillNature?.();
    setOptionalDialogOpen(false);
    props.onContinue();
  }

  function handleOptionalNo(): void {
    setOptionalDialogOpen(false);
    setOptionalOpen(true);
    firstNatureRef.current?.focus();
  }

  const continueDisabled = validateAppearance(props.appearance) !== null
    || (optionalOpen && validateNature(props.nature) !== null);

  const onRegisterContinue = props.onRegisterContinue;
  const shellOwnsContinue = props.shellOwnsContinue === true;
  useEffect(() => {
    if (!shellOwnsContinue || !onRegisterContinue) return;
    onRegisterContinue(handleContinue, continueDisabled);
  }, [shellOwnsContinue, onRegisterContinue, continueDisabled, optionalOpen, props.appearance, props.nature]);

  return (
    <section className="world-char-form" aria-label="Character look and nature">
      <h2>Detailed</h2>
      <p className="world-step-subtitle">Look</p>
      {loading && <p className="world-muted" aria-live="polite">Loading look options…</p>}

      <div className="world-field-row">
        <label htmlFor="character-species">Species</label>
        <select
          id="character-species"
          value={species}
          onChange={(e) => props.onSpeciesChange?.(e.target.value)}
        >
          {SPECIES_OPTIONS.map((option) => (
            <option key={option} value={option}>{displayLabel(option)}</option>
          ))}
        </select>
      </div>

      <div className="world-field-row">
        <label htmlFor="character-background">Background</label>
        <p className="world-muted">Please describe the character's background: where they were born, where they grew up, their heritage, other languages they speak, that kind of thing. It will affect your interactions.</p>
        <textarea
          id="character-background"
          value={props.background ?? ''}
          maxLength={500}
          onChange={(e) => props.onBackgroundChange?.(e.target.value)}
        />
      </div>

      <div className="world-field-row">
        <label htmlFor="skin-color">Skin color</label>
        <div className="world-action-row">
          {catalog.skinColors.map((color) => (
            <button
              key={color}
              type="button"
              className={props.appearance.skinColor === color ? 'btn-primary' : 'btn-ghost'}
              style={{ background: color, minWidth: 32, minHeight: 32 }}
              aria-label={`Skin ${color}`}
              onClick={() => props.onAppearanceChange({ ...props.appearance, skinColor: color })}
            />
          ))}
          <input
            id="skin-color"
            type="color"
            value={props.appearance.skinColor}
            onChange={(e) => props.onAppearanceChange({ ...props.appearance, skinColor: e.target.value })}
            aria-label="Custom skin color"
          />
        </div>
      </div>

      <div className="world-field-row">
        <label htmlFor="top-style">Top</label>
        <select
          id="top-style"
          value={props.appearance.topStyle}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, topStyle: e.target.value })}
        >
          {withCurrent(catalog.topStyles, props.appearance.topStyle).map((s) => <option key={s} value={s}>{displayLabel(s)}</option>)}
        </select>
      </div>
      <div className="world-field-row">
        <label htmlFor="top-color">Top color</label>
        <input
          id="top-color"
          type="color"
          value={props.appearance.topColor}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, topColor: e.target.value })}
        />
      </div>

      <div className="world-field-row">
        <label htmlFor="bottom-style">Bottom</label>
        <select
          id="bottom-style"
          value={props.appearance.bottomStyle}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, bottomStyle: e.target.value })}
        >
          {withCurrent(catalog.bottomStyles, props.appearance.bottomStyle).map((s) => <option key={s} value={s}>{displayLabel(s)}</option>)}
        </select>
      </div>
      <div className="world-field-row">
        <label htmlFor="bottom-color">Bottom color</label>
        <input
          id="bottom-color"
          type="color"
          value={props.appearance.bottomColor}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, bottomColor: e.target.value })}
        />
      </div>

      <div className="world-field-row">
        <label htmlFor="hair-style">{hairLabel}</label>
        <select
          id="hair-style"
          value={props.appearance.hairStyle}
          disabled={loading}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, hairStyle: e.target.value })}
        >
          {withCurrent(catalog.hairStyles, props.appearance.hairStyle).map((s) => <option key={s} value={s}>{displayLabel(s)}</option>)}
        </select>
      </div>
      <div className="world-field-row">
        <label htmlFor="hair-color">Hair color</label>
        <input
          id="hair-color"
          type="color"
          value={props.appearance.hairColor}
          onChange={(e) => props.onAppearanceChange({ ...props.appearance, hairColor: e.target.value })}
        />
      </div>

      <OptionalQuestionsToggle open={optionalOpen} onToggle={() => setOptionalOpen(!optionalOpen)} />
      {optionalOpen && (
        <div className="world-optional-section">
          <p className="world-step-subtitle">Nature</p>
          {NATURE_LABELS.map(([key, low, high], index) => (
            <div key={key} className="world-field-row">
              <div className="world-field-label-row">
                <span>{displayLabel(key)}</span>
                <span className="world-muted">
                  {props.nature[key]}/{max} · {props.nature[key] <= 4 ? low : props.nature[key] >= 7 ? high : balancedLabel(props.nature[key])}
                </span>
              </div>
              <input
                ref={index === 0 ? firstNatureRef : undefined}
                type="range"
                min={min}
                max={max}
                step={1}
                value={props.nature[key]}
                onChange={(e) => props.onNatureChange({ ...props.nature, [key]: Number(e.target.value) })}
                aria-label={displayLabel(key)}
              />
            </div>
          ))}
        </div>
      )}

      <TimedNotice text={props.error} className="world-error" role="alert" />

      <div className="world-action-row">
        <button type="button" className="btn-ghost" onClick={props.onAiSuggest}>Let AI fill in the blanks</button>
      </div>

      {!shellOwnsContinue && (
        <div className="world-action-row">
          <button
            type="button"
            className="btn-primary"
            disabled={continueDisabled}
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      )}

      <OptionalContinueDialog
        open={optionalDialogOpen}
        onYes={handleOptionalYes}
        onNo={handleOptionalNo}
      />
    </section>
  );
}

function withCurrent(options: readonly string[], current: string): string[] {
  if (!current || options.includes(current)) return [...options];
  return [current, ...options];
}

export function ensureDetailedDefaults(
  appearance: CharacterAppearance | null,
  nature: CharacterNature | null,
): { appearance: CharacterAppearance; nature: CharacterNature } {
  return {
    appearance: appearance ?? {
      skinColor: '#f1c27d', topStyle: 'shirt', topColor: '#4cc9f0',
      bottomStyle: 'trousers', bottomColor: '#3a506b', hairStyle: 'short', hairColor: '#3b2f2f',
    },
    nature: nature ?? blankNature(),
  };
}
